import type { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import FormProvider from '../../form/FormProvider';
import useForm from '../../form/useForm';
import { noop } from '../../lib';
import SaveShortcutScope, {
  SaveShortcutBoundary,
  type SaveShortcutPriority,
  type SaveShortcutStrategy,
} from '../SaveShortcutScope';
import FormBase from './FormBase';

interface GenericFormProps {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  onSubmit?: (values: Record<string, unknown>) => unknown;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown, info: { isInvalid: boolean }) => void;
  onInvalid?: () => void;
  schema?: unknown;
  defaultValues?: Record<string, unknown>;
  showServerErrors?: boolean;
  mapServerErrorField?: (field: string) => string;
  onNonFieldError?: (messages: string[]) => void;
  suppressEnterSubmit?: boolean;
  saveShortcut?: boolean;
  onShortcutSave?: () => unknown;
  shortcutBoundary?: {
    active?: boolean;
    priority: SaveShortcutPriority;
    strategy?: SaveShortcutStrategy;
  };
}

export type FormSubmitResult =
  | { ok: true; response: unknown }
  | {
      ok: false;
      error?: unknown;
      reason: 'invalid' | 'server';
    };

const Form = React.forwardRef<HTMLFormElement, GenericFormProps>(
  (props, ref) => {
    const {
      sx,
      children,
      onSubmit = noop as GenericFormProps['onSubmit'],
      onSuccess = noop as GenericFormProps['onSuccess'],
      onError = noop as GenericFormProps['onError'],
      onInvalid = noop,
      suppressEnterSubmit = false,
      saveShortcut = false,
      onShortcutSave,
      shortcutBoundary,
      ...restProps
    } = props;
    const formRef = React.useRef<HTMLFormElement>(null);

    const { handleSubmit, handleSubmitBase, ...form } = useForm(
      restProps as Record<string, unknown>,
    );
    React.useImperativeHandle(ref, () => formRef.current as HTMLFormElement);

    const submit = React.useCallback(
      () =>
        new Promise<FormSubmitResult>((resolve) => {
          const submitHandler = handleSubmit({
            onSubmit,
            onSuccess: (response) => {
              onSuccess?.(response);
              resolve({ ok: true, response });
            },
            onError: (error, info) => {
              onError?.(error, info);
              resolve({
                ok: false,
                error,
                reason: info.isInvalid ? 'invalid' : 'server',
              });
            },
            onInvalid: () => {
              onInvalid();
              resolve({ ok: false, reason: 'invalid' });
            },
          });

          void submitHandler().catch((error: unknown) => {
            resolve({ ok: false, error, reason: 'server' });
          });
        }),
      [handleSubmit, onError, onInvalid, onSubmit, onSuccess],
    );

    const validate = React.useCallback(
      () =>
        new Promise<
          { ok: true; values: Record<string, unknown> } | { ok: false }
        >((resolve) => {
          const validationHandler = handleSubmitBase(
            (values) => resolve({ ok: true, values }),
            () => {
              onInvalid();
              resolve({ ok: false });
            },
          );
          void validationHandler();
        }),
      [handleSubmitBase, onInvalid],
    );

    const formMethods = React.useMemo(
      () => ({
        clearErrors: form.clearErrors,
        control: form.control,
        getFieldState: form.getFieldState,
        getValues: form.getValues,
        handleSubmit,
        handleSubmitBase,
        register: form.register,
        reset: form.reset,
        resetField: form.resetField,
        setError: form.setError,
        setErrors: form.setErrors,
        setFocus: form.setFocus,
        setValue: form.setValue,
        submit,
        subscribe: form.subscribe,
        trigger: form.trigger,
        unregister: form.unregister,
        validate,
        watch: form.watch,
      }),
      [
        form.clearErrors,
        form.control,
        form.getFieldState,
        form.getValues,
        form.register,
        form.reset,
        form.resetField,
        form.setError,
        form.setErrors,
        form.setFocus,
        form.setValue,
        form.subscribe,
        form.trigger,
        form.unregister,
        form.watch,
        handleSubmit,
        handleSubmitBase,
        submit,
        validate,
      ],
    );

    const formElement = (
      <FormBase
        ref={formRef}
        sx={sx}
        suppressEnterSubmit={suppressEnterSubmit}
        onSubmit={() => {
          void submit();
        }}
      >
        {children}
      </FormBase>
    );

    const handleShortcutSave = () => {
      if (onShortcutSave) {
        return onShortcutSave();
      }

      return submit();
    };

    const content = (
      <FormProvider
        form={{ handleSubmit, handleSubmitBase, submit, validate, ...form }}
        methods={formMethods}
      >
        {saveShortcut ? (
          <SaveShortcutScope
            disabled={form.formState.isSubmitting}
            onSave={handleShortcutSave}
          >
            {formElement}
          </SaveShortcutScope>
        ) : (
          formElement
        )}
      </FormProvider>
    );

    if (!shortcutBoundary) {
      return content;
    }

    return (
      <SaveShortcutBoundary
        active={shortcutBoundary.active}
        priority={shortcutBoundary.priority}
        strategy={shortcutBoundary.strategy}
      >
        {content}
      </SaveShortcutBoundary>
    );
  },
);

Form.displayName = 'FormGeneric';
export default Form;
